import { DestroyRef, Directive, effect, ElementRef, inject, input } from '@angular/core';
import { Canvas } from 'fabric';
import { fitCanvasToBox } from 'shared/fabric-drawing/tools';

/**
 * Keeps a fabric canvas's DISPLAY size fitted (contain, never stretched) to the element
 * the directive sits on, at a fixed aspect ratio, via a `ResizeObserver`. Delegates the
 * sizing to the shared `fitCanvasToBox` (fabric `setDimensions({cssOnly})`), so the
 * canvas's internal resolution and pointer mapping are preserved; center the canvas with
 * flexbox on the host.
 *
 * Apply to the available-area element and bind the (imperatively-created) fabric canvas:
 * ```html
 * <div class="fit-host" [pcpFabricCanvasFit]="canvas()" [fabricCanvasFitAspectRatio]="16/9">
 *   <canvas #canvasEl></canvas>
 * </div>
 * ```
 *
 * Lives beside the renderers that use it; the underlying math is shared with PracticeTrainer
 * via `fitCanvasToBox` from `shared/fabric-drawing/tools`.
 */
@Directive({
    selector: '[pcpFabricCanvasFit]',
})
export class FabricCanvasFitDirective {
    private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);

    /**
     * The fabric canvas to size (the directive's selector binding). Null/undefined until
     * it exists → no-op. Property name matches the selector, so no alias is needed.
     */
    readonly pcpFabricCanvasFit = input<Canvas | null | undefined>(undefined);
    /** Logical aspect ratio (width / height). Default 16:9. */
    readonly fabricCanvasFitAspectRatio = input(16 / 9);

    private readonly observer = new ResizeObserver(() => this.fit());

    constructor() {
        this.observer.observe(this.hostRef.nativeElement);
        // Refit on first canvas assignment and whenever the canvas / aspect ratio changes.
        effect(() => {
            this.pcpFabricCanvasFit();
            this.fabricCanvasFitAspectRatio();
            this.fit();
        });
        inject(DestroyRef).onDestroy(() => this.observer.disconnect());
    }

    private fit(): void {
        const canvas = this.pcpFabricCanvasFit();
        if (!canvas) return;
        const el = this.hostRef.nativeElement;
        fitCanvasToBox(canvas, el.clientWidth, el.clientHeight, this.fabricCanvasFitAspectRatio());
    }
}
